import { FormGroup, PasswordInput, Select, TextInput } from '@ksyos/design-system';
import type { IKisSettingAddEditValidated } from '@common/validation/kisSetting';
import type { ValidationErrors } from '@frontend/utils/validation';
import { KisType } from '@common/enums/kisType';
import { KisSettingAddEditDefault } from '@common/validation/kisSetting';
import { InputCheckbox } from '@frontend/components/elements/common';
import { GroupSectionHeading } from '@frontend/components/elements/GroupSectionHeading';
import { getSelectedLanguage } from '@frontend/translate';
import { getValidationErrorTranslation } from '@frontend/utils/getValidationErrorTranslation';
import type { IGroup } from './GroupsAddEdit';

type KisSettingAddEditProps = {
    errors: ValidationErrors;
    handleChange: <K extends keyof IGroup>(name: K, value: IGroup[K]) => Promise<void>;
    handleKisSettingChange: <K extends keyof IKisSettingAddEditValidated>(
        name: K,
        value: IKisSettingAddEditValidated[K]
    ) => Promise<void>;
    kisSetting?: IKisSettingAddEditValidated;
    validateOne: (name: string) => Promise<void>;
};

export function KisSettingAddEdit(props: Readonly<KisSettingAddEditProps>) {
    const { errors, kisSetting, handleChange, handleKisSettingChange, validateOne } = props;

    const translate = getSelectedLanguage();

    return (
        <>
            <GroupSectionHeading sectionName={translate.kisSetting.kisSetting} />
            <FormGroup label={translate.kisSetting.usesKis}>
                <InputCheckbox
                    checked={!!kisSetting}
                    onChange={async (enabled) => {
                        if (enabled) {
                            await handleChange('kisSetting', KisSettingAddEditDefault);
                        } else {
                            await handleChange('kisSetting', undefined);
                        }
                    }}
                />
            </FormGroup>

            {!!kisSetting && (
                <>
                    <FormGroup
                        hasErrors={errors.has('kisSetting.type')}
                        label={translate.kisSetting.kisType}
                    >
                        <Select
                            error={getValidationErrorTranslation(errors.get('kisSetting.type'))}
                            onBlur={async () => await validateOne('kisSetting.type')}
                            onChange={async (option) => {
                                if (option) {
                                    await handleKisSettingChange('type', option.value);
                                }
                            }}
                            options={[
                                { label: KisType.Care2U, value: KisType.Care2U },
                                { label: KisType.PortaVita, value: KisType.PortaVita },
                                { label: KisType.CareSharing, value: KisType.CareSharing },
                                { label: KisType.TeleDia, value: KisType.TeleDia },
                            ]}
                            value={
                                kisSetting.type
                                    ? {
                                          label: kisSetting.type,
                                          value: kisSetting.type,
                                      }
                                    : null
                            }
                        />
                    </FormGroup>

                    <FormGroup
                        hasErrors={errors.has('kisSetting.serverAddress')}
                        label={translate.kisSetting.serverAddress}
                    >
                        <TextInput
                            error={getValidationErrorTranslation(errors.get('kisSetting.serverAddress'))}
                            onBlur={async () => await validateOne('kisSetting.serverAddress')}
                            onChange={async (e) => await handleKisSettingChange('serverAddress', e.target.value)}
                            placeholder={translate.kisSetting.serverAddress}
                            value={kisSetting.serverAddress}
                        />
                    </FormGroup>

                    <FormGroup
                        hasErrors={errors.has('kisSetting.serverPort')}
                        label={translate.kisSetting.serverPort}
                    >
                        <TextInput
                            error={getValidationErrorTranslation(errors.get('kisSetting.serverPort'))}
                            onBlur={async () => await validateOne('kisSetting.serverPort')}
                            onChange={async (e) => await handleKisSettingChange('serverPort', e.target.value)}
                            placeholder={translate.kisSetting.serverPort}
                            value={kisSetting.serverPort}
                        />
                    </FormGroup>
                    <FormGroup
                        hasErrors={errors.has('kisSetting.username')}
                        label={translate.kisSetting.username}
                    >
                        <TextInput
                            error={getValidationErrorTranslation(errors.get('kisSetting.username'))}
                            onBlur={async () => await validateOne('kisSetting.username')}
                            onChange={async (e) =>
                                await handleKisSettingChange(
                                    'username',
                                    e.target.value.length > 0 ? e.target.value : null
                                )
                            }
                            placeholder={translate.kisSetting.username}
                            value={kisSetting.username ?? ''}
                        />
                    </FormGroup>
                    <FormGroup
                        hasErrors={errors.has('kisSetting.password')}
                        label={translate.common.password}
                    >
                        <PasswordInput
                            error={getValidationErrorTranslation(errors.get('kisSetting.password'))}
                            onBlur={async () => await validateOne('kisSetting.password')}
                            onChange={async (e) =>
                                await handleKisSettingChange(
                                    'password',
                                    e.target.value.length > 0 ? e.target.value : null
                                )
                            }
                            placeholder={translate.common.password}
                            value={kisSetting.password ?? ''}
                        />
                    </FormGroup>
                </>
            )}
        </>
    );
}
